// ./src/components/features/Tags/TagsList/TagsList.tsx
//
// Tags list with a different presentation per breakpoint, switched live on
// window resize (useAppEnvironment is backed by useMediaQuery):
//
// - Desktop (>= md): a compact TABLE — swatch · name · created · row actions.
// - Tablet / mobile (< md): a CARD list.
//
// Interaction (best-practice, tap-safe):
// - Rows/cards are NEVER click-to-edit. Editing happens only via the explicit
//   Edit button, so an accidental tap can't open the form.
// - Desktop + tablet: Edit/Delete buttons are always visible.
// - Phones (< sm): swipe-to-reveal Edit/Delete; tapping the card does nothing.
//
// Data resolves from <TagsProvider> when props are omitted.

import React, { useCallback, useMemo } from "react";
import {
  Paper,
  Stack,
  Group,
  Text,
  Box,
  ActionIcon,
  Tooltip,
  Skeleton,
  Table,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";

import { Tag } from "@/utils/api";
import { useOptionalTagsContext, useAppEnvironment } from "@/contexts";
import {
  AppIcon,
  ColorSwatch,
  EmptyState,
  SwipeableCard,
  SwipeAction,
  useSwipeableList,
  LoadMoreButton,
  CardSkeletonActions,
  CardSkeletonLines,
  countCardActions,
} from "@/components/generic";
import {
  useEntranceAnimation,
  entranceListVariants,
  entranceCardVariants,
} from "@/hooks/useEntranceAnimation";
import {
  formatSmartDateTime,
  recordHasRight,
  HIGHLIGHT_STYLE,
  HIGHLIGHT_ROW_BG,
} from "@/utils";

// ============================================
// Types
// ============================================

export interface TagsListProps {
  /** Array of tag records. Optional when inside provider. */
  tags?: Tag[];

  /** Loading state for initial load. Optional when inside provider. */
  loading?: boolean;

  /** Loading state for loading more items. Optional — reads from context. */
  loadingMore?: boolean;

  /** Whether there are more items to load. Optional — reads from context. */
  hasMore?: boolean;

  /** Callback to load more items. Optional — reads from context. */
  onLoadMore?: () => void;

  /** Callback when the Edit action is invoked (button or swipe). */
  onEditTag?: (tag: Tag) => void;

  /** Callback when the Delete action is invoked (button or swipe). */
  onDeleteTag?: (tag: Tag) => void;

  /** Predicate for whether a tag is being removed. Optional — reads from context. */
  isRemoving?: (id?: string) => boolean;

  /** ID of recently edited/added tag to highlight */
  highlightedTagId?: string | null;

  /** Callback when add button is clicked (for empty state) */
  onAddTag?: () => void;
  /**
   * A search term or filter is narrowing the list. An empty result then means "no match" — NOT
   * "nothing here yet", so the first-run Add prompt would be telling somebody who just searched
   * that their records are gone. ⚠ The list cannot see the page's search box; this is a PROP.
   */
  hasQueryNarrowing?: boolean;
}

// ============================================
// Shared bits
// ============================================

// Always-visible row actions (desktop table + tablet cards). Explicit buttons
// are the ONLY way to edit/delete, so there's nothing to discover on hover.
const RowActions: React.FC<{
  tag: Tag;
  onEdit?: (tag: Tag) => void;
  onDelete?: (tag: Tag) => void;
  isDeleting: boolean;
}> = ({ tag, onEdit, onDelete, isDeleting }) => {
  const { t } = useTranslation(["common"]);

  // Per-record rights (from the API) gate the buttons on top of page-level rights.
  const showEdit = !!onEdit && recordHasRight(tag, "edit");
  const showDelete = !!onDelete && recordHasRight(tag, "remove");

  if (!showEdit && !showDelete) return null;

  return (
    <Group gap={4} wrap="nowrap">
      {showEdit && onEdit && (
        <Tooltip label={t("common:buttons.edit")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="blue"
            size="md"
            onClick={() => onEdit(tag)}
            disabled={isDeleting}
            aria-label={t("common:buttons.edit")}
          >
            <AppIcon iconName="Edit" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
      {showDelete && onDelete && (
        <Tooltip label={t("common:buttons.delete")} withArrow position="bottom">
          <ActionIcon
            variant="subtle"
            color="red"
            size="md"
            onClick={() => onDelete(tag)}
            loading={isDeleting}
            disabled={isDeleting}
            aria-label={t("common:buttons.delete")}
          >
            <AppIcon iconName="Trash" size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </Group>
  );
};

// ============================================
// Shared row shape
// ============================================

interface RowProps {
  tag: Tag;
  onEdit?: (tag: Tag) => void;
  onDelete?: (tag: Tag) => void;
  highlighted: boolean;
  isDeleting: boolean;
  /** i18n language for the localized timestamp. */
  language: string;
}

// ============================================
// Desktop: Table row (not clickable)
// ============================================

const TagTableRow: React.FC<RowProps> = ({
  tag,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  language,
}) => {
  return (
    <Table.Tr
      style={{
        opacity: isDeleting ? 0.5 : 1,
        backgroundColor: highlighted ? HIGHLIGHT_ROW_BG : undefined,
        transition: "background-color 150ms ease, opacity 150ms ease",
      }}
    >
      <Table.Td style={{ width: 52 }}>
        <ColorSwatch color={tag.color} size={26} />
      </Table.Td>
      <Table.Td>
        <Text size="md" fw={500} lineClamp={1}>
          {tag.name || "—"}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 210 }}>
        <Text size="sm" c="dimmed">
          {formatSmartDateTime(tag.createdAt, language)}
        </Text>
      </Table.Td>
      <Table.Td style={{ width: 96 }}>
        <RowActions
          tag={tag}
          onEdit={onEdit}
          onDelete={onDelete}
          isDeleting={isDeleting}
        />
      </Table.Td>
    </Table.Tr>
  );
};

// A skeleton row that IS a real Table.Tr — identical column widths, spacing and
// element sizes to TagTableRow, so the loading table matches the loaded one.
const TagTableRowSkeleton: React.FC = () => (
  <Table.Tr>
    <Table.Td style={{ width: 52 }}>
      <Skeleton height={26} width={26} radius="sm" />
    </Table.Td>
    <Table.Td>
      <Skeleton height={16} width="60%" maw={220} />
    </Table.Td>
    <Table.Td style={{ width: 210 }}>
      <Skeleton height={16} width={150} />
    </Table.Td>
    <Table.Td style={{ width: 96 }}>
      <Group gap={4} wrap="nowrap">
        <Skeleton circle height={28} width={28} />
        <Skeleton circle height={28} width={28} />
      </Group>
    </Table.Td>
  </Table.Tr>
);

// ============================================
// Mobile / tablet: Card (not clickable)
// ============================================

interface CardProps extends RowProps {
  forceClose?: boolean;
  onForceCloseComplete?: () => void;
  onSwipeStateChange?: (isOpen: boolean) => void;
}

const TagCardComponent: React.FC<CardProps> = ({
  tag,
  onEdit,
  onDelete,
  highlighted,
  isDeleting,
  language,
  forceClose,
  onForceCloseComplete,
  onSwipeStateChange,
}) => {
  const { t } = useTranslation(["common"]);

  const swipeActions = useMemo((): SwipeAction[] => {
    const actions: SwipeAction[] = [];
    if (onEdit && recordHasRight(tag, "edit")) {
      actions.push({
        key: "edit",
        icon: "Edit",
        label: t("common:buttons.edit"),
        color: "blue",
        onClick: () => onEdit(tag),
        disabled: isDeleting,
      });
    }
    if (onDelete && recordHasRight(tag, "remove")) {
      actions.push({
        key: "delete",
        icon: "Trash",
        label: t("common:buttons.delete"),
        color: "red",
        onClick: () => onDelete(tag),
        loading: isDeleting,
        disabled: isDeleting,
      });
    }
    return actions;
  }, [onEdit, onDelete, tag, isDeleting, t]);

  // showActions=false on phones (swipe reveals them); true on tablet.
  const cardBody = (showActions: boolean) => (
    <Paper
      p="md"
      withBorder
      radius="md"
      opacity={isDeleting ? 0.5 : 1}
      style={{
        transition: "opacity 150ms ease, box-shadow 150ms ease",
        ...(highlighted ? HIGHLIGHT_STYLE : {}),
      }}
    >
      <Group gap="md" wrap="nowrap" align="center">
        <ColorSwatch color={tag.color} />
        <Box style={{ flex: 1, minWidth: 0 }}>
          <Text size="md" fw={500} lineClamp={1}>
            {tag.name || "—"}
          </Text>
          <Text size="xs" c="dimmed">
            {formatSmartDateTime(tag.createdAt, language)}
          </Text>
        </Box>
        {showActions && (
          <RowActions
            tag={tag}
            onEdit={onEdit}
            onDelete={onDelete}
            isDeleting={isDeleting}
          />
        )}
      </Group>
    </Paper>
  );

  if (swipeActions.length > 0) {
    return (
      <>
        {/* Phones: swipe to reveal actions (no visible buttons, no tap-to-edit) */}
        <Box hiddenFrom="sm">
          <SwipeableCard
            leftSwipeActions={swipeActions}
            forceClose={forceClose}
            onForceCloseComplete={onForceCloseComplete}
            onSwipeStateChange={onSwipeStateChange}
            disabled={isDeleting}
            borderRadius="var(--mantine-radius-md)"
          >
            {cardBody(false)}
          </SwipeableCard>
        </Box>
        {/* Tablet: always-visible action buttons */}
        <Box visibleFrom="sm">{cardBody(true)}</Box>
      </>
    );
  }

  return cardBody(false);
};

const TagCard = React.memo(TagCardComponent);

// A skeleton card matching TagCardComponent's cardBody: same padding, 32px
// swatch, two-line name+date block, and tablet-only action buttons.
const TagCardSkeleton: React.FC<{ actions: number }> = ({ actions }) => (
  <Paper p="md" withBorder radius="md">
    <Group gap="md" wrap="nowrap" align="center">
      <Skeleton height={32} width={32} radius="sm" />
      <CardSkeletonLines count={2} sizes={["md", "xs"]} gap={0} />
      <CardSkeletonActions count={actions} />
    </Group>
  </Paper>
);

// ============================================
// Main Component
// ============================================

const TagsListComponent: React.FC<TagsListProps> = ({
  tags: tagsProp,
  loading: loadingProp,
  loadingMore: loadingMoreProp,
  hasMore: hasMoreProp,
  onLoadMore: onLoadMoreProp,
  onEditTag,
  onDeleteTag,
  isRemoving: isRemovingProp,
  highlightedTagId,
  onAddTag,
  hasQueryNarrowing = false,
}) => {
  const { t, i18n } = useTranslation(["tags", "common"]);
  const { isDesktop } = useAppEnvironment();
  const language = i18n.language;

  // ── Context vs Props resolution ──
  const tagsContext = useOptionalTagsContext();

  const tags = tagsProp ?? tagsContext?.tags ?? [];
  const loading = loadingProp ?? tagsContext?.loading ?? false;
  const loadingMore = loadingMoreProp ?? tagsContext?.loadingMore ?? false;
  const hasMore = hasMoreProp ?? tagsContext?.hasMore ?? false;
  const onLoadMore = onLoadMoreProp ?? tagsContext?.loadMore;
  const isRemoving = isRemovingProp ?? tagsContext?.isRemoving ?? (() => false);

  const {
    openCard,
    closeAllCards,
    shouldForceClose,
    handleForceCloseComplete,
  } = useSwipeableList();

  const { hasAnimated } = useEntranceAnimation({ itemCount: tags.length });

  // ── Wrapped handlers (close any open swipe before acting) ──
  const handleEdit = useCallback(
    (tag: Tag) => {
      closeAllCards();
      onEditTag?.(tag);
    },
    [onEditTag, closeAllCards]
  );

  const handleDelete = useCallback(
    (tag: Tag) => {
      closeAllCards();
      onDeleteTag?.(tag);
    },
    [onDeleteTag, closeAllCards]
  );

  const handleSwipeStateChange = useCallback(
    (tagId: string, isOpen: boolean) => {
      if (isOpen) openCard(tagId);
    },
    [openCard]
  );

  // ⚠ A SKELETON IS FOR THE FIRST READ ONLY. `loading && x.length === 0` re-shows it on every
  // refetch of an EMPTY list, so an empty page reads skeleton → "none" → skeleton → "none". Once
  // the first read is back, a refetch keeps the rows OR the empty state and ContentLoadingOverlay
  // says it is reloading. ⚠ The fallback answers "is this all the data there will ever be?" for a
  // caller with no provider. ▶ npm run check-list-states
  const isLoaded = tagsContext?.isLoaded ?? tagsProp !== undefined;
  const showSkeleton = !isLoaded;

  // ── Empty ──
  if (!showSkeleton && tags.length === 0) {
    if (hasQueryNarrowing) {
      return (
        <Text size="md" c="dimmed" ta="center" p="md">
          {t("tags:emptyState.noMatch")}
        </Text>
      );
    }

    return (
      <Paper withBorder p="xl" radius="md">
        <EmptyState
          title={t("tags:emptyState.title")}
          description={t("tags:emptyState.description")}
          iconName="Tag"
          actionLabel={t("tags:emptyState.actionLabel")}
          onAction={onAddTag}
          size="md"
        />
      </Paper>
    );
  }

  const rowProps = (tag: Tag): RowProps => ({
    tag,
    onEdit: onEditTag ? handleEdit : undefined,
    onDelete: onDeleteTag ? handleDelete : undefined,
    highlighted: tag.id === highlightedTagId,
    isDeleting: isRemoving(tag.id),
    language,
  });

  const loadMore = (
    <LoadMoreButton
      hasMore={hasMore}
      loading={loading}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
    />
  );

  // ── Desktop: table ──
  if (isDesktop) {
    return (
      <Stack gap="md">
        <Paper withBorder radius="md" style={{ overflow: "hidden" }}>
          <Table verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 52 }} />
                <Table.Th>{t("tags:list.columns.name")}</Table.Th>
                <Table.Th style={{ width: 210 }}>
                  {t("tags:list.columns.createdAt")}
                </Table.Th>
                <Table.Th style={{ width: 96 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {showSkeleton
                ? Array.from({ length: 6 }).map((_, i) => (
                    <TagTableRowSkeleton key={i} />
                  ))
                : tags.map((tag) => (
                    <TagTableRow key={tag.id} {...rowProps(tag)} />
                  ))}
            </Table.Tbody>
          </Table>
        </Paper>
        {!showSkeleton && loadMore}
      </Stack>
    );
  }

  // ── Tablet / mobile: cards ──
  if (showSkeleton) {
    return (
      <Stack gap="sm">
        {Array.from({ length: 5 }).map((_, i) => (
          <TagCardSkeleton key={i} actions={countCardActions(onEditTag, onDeleteTag)} />
        ))}
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <motion.div
        variants={hasAnimated ? undefined : entranceListVariants}
        initial={hasAnimated ? false : "hidden"}
        animate={hasAnimated ? undefined : "visible"}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--mantine-spacing-sm)",
        }}
      >
        <AnimatePresence mode="sync">
          {tags.map((tag) => (
            <motion.div
              key={tag.id}
              variants={hasAnimated ? undefined : entranceCardVariants}
              initial={hasAnimated ? false : "hidden"}
              animate={hasAnimated ? undefined : "visible"}
              exit="exit"
              layout
            >
              <TagCard
                {...rowProps(tag)}
                forceClose={shouldForceClose(tag.id || "")}
                onForceCloseComplete={() =>
                  handleForceCloseComplete(tag.id || "")
                }
                onSwipeStateChange={(isOpen) =>
                  handleSwipeStateChange(tag.id || "", isOpen)
                }
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </motion.div>
      {loadMore}
    </Stack>
  );
};

export const TagsList = React.memo(TagsListComponent);
